import * as React from "react"
import { updateProfile, updateStudentByProfessor } from "../auth/api"
import { getGraduacoes } from "../auth/api"

const inputClass =
  "w-full border-2 border-slate-200 rounded-xl text-center p-2 mt-1 bg-white text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-cor-base transition-colors"

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
        {label}
      </label>
      {children}
    </div>
  )
}

export default function EditProfileModal({
  profile,
  onClose,
  onSaved,
  studentId,
}) {
  const [graduacoes, setGraduacoes] = React.useState(["fase inicial"])
  const [form, setForm] = React.useState({
    nomeCompleto: profile?.nomeCompleto || "",
    cpf: profile?.cpf || "",
    dataNascimento: profile?.dataNascimento || "",
    celular: profile?.celular || "",
    graduacao: profile?.graduacao || "fase inicial",
    ultimoExameEm: profile?.ultimoExameEm || "",
    email: profile?.email || "",
  })
  const [error, setError] = React.useState("")
  const [loading, setLoading] = React.useState(false)
  const [success, setSuccess] = React.useState(false)

  React.useEffect(() => {
    getGraduacoes()
      .then((list) => {
        if (list?.length) setGraduacoes(list)
      })
      .catch(() => {})
  }, [])

  // Fecha ao clicar fora
  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  // Fecha com Escape
  React.useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onClose])

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const formatarCPF = (valor) => {
    const n = valor.replace(/\D/g, "")
    if (n.length <= 3) return n
    if (n.length <= 6) return `${n.slice(0, 3)}.${n.slice(3)}`
    if (n.length <= 9) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`
    return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9, 11)}`
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError("")
    setSuccess(false)
    try {
      setLoading(true)
      const payload = {
        nomeCompleto: form.nomeCompleto.trim(),
        cpf: form.cpf.replace(/\D/g, ""),
        dataNascimento: form.dataNascimento,
        celular: form.celular.replace(/\D/g, ""),
        graduacao: form.graduacao,
        ultimoExameEm: form.ultimoExameEm || null,
        email: form.email.trim(),
      }
      const updated = studentId
        ? await updateStudentByProfessor(studentId, payload)
        : await updateProfile(payload)
      setSuccess(true)
      onSaved(updated?.user || updated?.profile || { ...profile, ...form })
      setTimeout(() => onClose(), 1200)
    } catch (err) {
      setError(err?.message || "Não foi possível salvar as alterações.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}>
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-cor-base rounded-t-2xl">
          <h2 className="text-xl font-oswald font-semibold text-white tracking-wide">
            Alterar dados
          </h2>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-2xl leading-none transition-colors"
            aria-label="Fechar"
            type="button">
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 grid gap-4">
          <Field label="Nome completo">
            <input
              className={inputClass}
              value={form.nomeCompleto}
              onChange={(e) => update("nomeCompleto", e.target.value)}
              required
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="CPF">
              <input
                className={inputClass}
                value={formatarCPF(form.cpf)}
                onChange={(e) => {
                  const n = e.target.value.replace(/\D/g, "")
                  if (n.length <= 11) update("cpf", n)
                }}
                inputMode="numeric"
                maxLength={14}
                required
              />
            </Field>

            <Field label="Data de nascimento">
              <input
                type="date"
                className={inputClass}
                value={form.dataNascimento}
                onChange={(e) => update("dataNascimento", e.target.value)}
                required
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Celular">
              <input
                className={inputClass}
                value={form.celular}
                onChange={(e) => {
                  const n = e.target.value.replace(/\D/g, "")
                  if (n.length <= 11) update("celular", n)
                }}
                inputMode="tel"
                maxLength={11}
                placeholder="DDD + número"
                required
              />
            </Field>

            {studentId ? (
              <Field label="Graduação">
                <select
                  className={inputClass}
                  value={form.graduacao}
                  onChange={(e) => update("graduacao", e.target.value)}
                  required>
                  {graduacoes.map((g) => (
                    <option key={g} value={g}>
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <Field label="Graduação">
                <input
                  className={`${inputClass} bg-slate-100 text-slate-400 cursor-not-allowed`}
                  value={
                    form.graduacao
                      ? form.graduacao.charAt(0).toUpperCase() +
                        form.graduacao.slice(1)
                      : ""
                  }
                  readOnly
                  tabIndex={-1}
                />
              </Field>
            )}
          </div>

          <Field label="Data do último exame (opcional)">
            <input
              type="date"
              className={inputClass}
              value={form.ultimoExameEm}
              onChange={(e) => update("ultimoExameEm", e.target.value)}
            />
            <p className="text-xs text-slate-400 mt-1">
              Deixe em branco se nunca fez exame.
            </p>
          </Field>

          <Field label="E-mail">
            <input
              type="email"
              className={inputClass}
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              required
            />
          </Field>

          {/* Feedback */}
          {error && (
            <p className="text-red-600 text-center text-sm font-semibold">
              {error}
            </p>
          )}
          {success && (
            <p className="text-green-600 text-center text-sm font-semibold">
              Dados atualizados com sucesso!
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-all">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 rounded-xl bg-cor-base text-white font-semibold hover:scale-[1.02] active:scale-[.97] transition-all disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? "Salvando..." : "Salvar alterações"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
